/**
 * Document-numbering helpers shared by invoices, estimates, and expenses.
 *
 * These documents use a single monotonic integer sequence that starts at a
 * floor (1000) and never reuses a value, even when manual edits leave gaps.
 */

/**
 * Picks the next unused document number given every number already in use.
 *
 * Non-numeric / non-positive / empty raw values are ignored. The result is at
 * least `floor`, otherwise one above the current maximum, and is guaranteed not
 * to collide with an existing value.
 *
 * @example
 * nextSequentialNumber([]); // "1000" — empty starts at the floor
 * nextSequentialNumber(["1000", "1001"]); // "1002"
 * nextSequentialNumber(["500"]); // "1000" — below the floor is ignored
 * nextSequentialNumber(["1000", null, "abc"]); // "1001" — junk skipped
 */
export function nextSequentialNumber(rawValues: unknown[], floor = 1000): string {
  const existing = new Set<number>();
  for (const raw of rawValues) {
    if (!raw) continue;
    const parsed = parseInt(String(raw), 10);
    if (!Number.isNaN(parsed) && parsed > 0) existing.add(parsed);
  }

  const max = existing.size > 0 ? Math.max(...existing) : 0;
  let next = Math.max(max + 1, floor);
  while (existing.has(next)) next++;

  return String(next);
}
